import { useState, useEffect, useCallback } from "react";

interface Notice {
  id: number;
  badge: string;
  title: string;
  content: string;
  created_at: string;
  is_pinned: boolean;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

const BADGE_COLORS: Record<string, string> = {
  공지: "bg-violet-500/15 text-violet-300 border-violet-500/25",
  업데이트: "bg-sky-500/15 text-sky-300 border-sky-500/25",
  이벤트: "bg-amber-500/15 text-amber-300 border-amber-500/25",
  점검: "bg-red-500/15 text-red-300 border-red-500/25",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function isNewNotice(createdAt: string) {
  const created = new Date(createdAt);
  const now = new Date();
  return (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24) <= 7;
}

export default function NoticeSection() {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState<Notice | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  const fetchNotices = useCallback(async () => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-public-notices`, {
        headers: { apikey: SUPABASE_ANON_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setNotices(json.notices ?? []);
    } catch (err) {
      console.error("[NoticeSection] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const pinned = notices.filter((n) => n.is_pinned);
  const regular = notices.filter((n) => !n.is_pinned);
  const displayList = [...pinned, ...regular].slice(0, 5);

  if (!loading && notices.length === 0) return null;

  return (
    <section className="bg-[#080812] border-t border-white/[0.06] py-14 md:py-20">
      <div className="max-w-5xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 border border-rose-500/20">
              <i className="ri-notification-3-line text-rose-400 text-sm"></i>
            </div>
            <h2 className="text-white font-semibold text-base md:text-lg">공지사항</h2>
          </div>
          <button
            onClick={() => setAllOpen(true)}
            className="text-gray-500 hover:text-white text-sm flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
          >
            모든 공지 보기
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 animate-pulse flex items-center gap-3">
                <div className="w-12 h-5 bg-white/[0.06] rounded-full shrink-0"></div>
                <div className="flex-1 h-4 bg-white/[0.06] rounded"></div>
                <div className="w-20 h-4 bg-white/[0.06] rounded shrink-0"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2.5">
            {displayList.map((n) => {
              const badgeStyle = BADGE_COLORS[n.badge] || "bg-white/[0.06] text-gray-400 border-white/[0.1]";
              const newDot = isNewNotice(n.created_at);
              return (
                <button
                  key={n.id}
                  onClick={() => setDetail(n)}
                  className="w-full flex items-center gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/[0.12] rounded-xl px-4 py-3.5 transition-all cursor-pointer text-left group"
                >
                  <div className="flex items-center gap-2 shrink-0">
                    {n.is_pinned && (
                      <i className="ri-pushpin-2-fill text-rose-400 text-xs"></i>
                    )}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border whitespace-nowrap ${badgeStyle}`}>
                      {newDot && !n.is_pinned && <span className="w-1.5 h-1.5 rounded-full bg-pink-400 inline-block"></span>}
                      {n.badge}
                    </span>
                  </div>
                  <p className="text-gray-300 text-sm truncate flex-1 group-hover:text-white transition-colors">
                    {n.title}
                  </p>
                  <span className="text-gray-600 text-xs shrink-0 hidden sm:block">
                    {formatDate(n.created_at)}
                  </span>
                  <div className="w-4 h-4 flex items-center justify-center text-gray-600 group-hover:text-gray-400 transition-colors shrink-0">
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)}></div>
          <div
            className="relative bg-[#13131f] border border-white/[0.1] rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#13131f] border-b border-white/[0.07] p-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-2.5">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border whitespace-nowrap ${BADGE_COLORS[detail.badge] || "bg-white/[0.06] text-gray-400 border-white/[0.1]"}`}>
                  {detail.badge}
                </span>
                <h3 className="text-white font-bold text-sm">공지사항</h3>
              </div>
              <button
                onClick={() => setDetail(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-white font-semibold text-base">{detail.title}</p>
              <p className="text-gray-400 text-sm leading-relaxed whitespace-pre-line">{detail.content}</p>
              <div className="flex items-center gap-2 text-gray-600 text-xs pt-2 border-t border-white/[0.06]">
                <i className="ri-time-line"></i>
                <span>{formatDate(detail.created_at)}</span>
                {detail.is_pinned && (
                  <span className="flex items-center gap-1 text-rose-400">
                    <i className="ri-pushpin-2-fill"></i>상단 고정
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Notices Modal */}
      {allOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setAllOpen(false)}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAllOpen(false)}></div>
          <div
            className="relative bg-[#13131f] border border-white/[0.1] rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-[#13131f] border-b border-white/[0.07] p-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-rose-500/10 border border-rose-500/20">
                  <i className="ri-notification-3-line text-rose-400 text-sm"></i>
                </div>
                <h3 className="text-white font-bold text-sm">모든 공지사항</h3>
              </div>
              <button
                onClick={() => setAllOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="p-5 space-y-2.5">
              {notices.length === 0 ? (
                <p className="text-gray-600 text-sm text-center py-8">등록된 공지사항이 없습니다.</p>
              ) : (
                notices.map((n) => {
                  const badgeStyle = BADGE_COLORS[n.badge] || "bg-white/[0.06] text-gray-400 border-white/[0.1]";
                  return (
                    <button
                      key={n.id}
                      onClick={() => {
                        setAllOpen(false);
                        setTimeout(() => setDetail(n), 200);
                      }}
                      className="w-full flex items-start gap-3 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.08] hover:border-white/[0.12] rounded-xl px-4 py-3.5 transition-all cursor-pointer text-left group"
                    >
                      <div className="flex items-center gap-2 mt-0.5 shrink-0">
                        {n.is_pinned && <i className="ri-pushpin-2-fill text-rose-400 text-xs"></i>}
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border whitespace-nowrap ${badgeStyle}`}>
                          {n.badge}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-300 text-sm group-hover:text-white transition-colors truncate">
                          {n.title}
                        </p>
                        <p className="text-gray-600 text-xs mt-0.5 line-clamp-1">{n.content}</p>
                      </div>
                      <span className="text-gray-600 text-xs shrink-0 mt-0.5 hidden sm:block">{formatDate(n.created_at)}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}